import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useLocation } from "react-router-dom";

import { useUploadFile } from "src/api/filesApi/queries";
import { useReviewCase, useReviewCases } from "src/api/queries";
import {
  AccordionRoot,
  EditorAccordionItem as AccordionItem,
  accordionRootClassName,
} from "src/base-components/EditorAccordionItem";
import { Icon } from "src/base-components/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { ManualReviewHighlights } from "src/entities/entityView/Highlights";
import { ManualReviewInspectData } from "src/entities/entityView/InspectData";
import { ReviewCaseDetails } from "src/entities/entityView/ReviewCaseDetails";
import { decisionsQueryKeys } from "src/entities/entityView/useEntityTabRefresh";
import { entitiesKeys } from "src/entities/queries";
import { Environment, useEnvironment } from "src/hooks/useEnvironment";
import { ReviewCaseActionForm } from "src/manualReview/ReviewCaseContent";
import { FormSide } from "src/manualReview/reviewCaseCommon/FormSide";
import { ManualReviewAccordionHeader } from "src/manualReviewNode/ManualReviewNodeEditor";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  EntityViewParams,
  getBaseUrl,
  getUrlToReviewCase,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type ManualReviewTabProps = {
  flowSlug: string;
  decisionId: string;
  flowId: string;
  onClose: () => void;
};

export const ManualReviewTabContent: React.FC<ManualReviewTabProps> = ({
  flowSlug,
  decisionId,
  flowId,
  onClose,
}) => {
  const { orgId, schema, id: entityId } = useParamsDecode<EntityViewParams>();
  const { workspace } = useWorkspaceContext();
  const { search } = useLocation();
  const queryClient = useQueryClient();
  const fileUpload = useUploadFile();
  const [env] = useEnvironment();

  const { data: reviewCases, isLoading: isLoadingList } = useReviewCases(
    workspace.base_url,
    flowSlug,
    {
      entity_or_decision_id: decisionId,
    },
  );
  const caseId = reviewCases?.pages[0]?.cases[0]?.id;

  // Then fetch the single case directly (like ReviewCaseContent does)
  // This ensures the cache updates immediately when mutations complete
  const singleCaseQuery = useReviewCase(
    caseId ? workspace.base_url : undefined,
    flowSlug,
    caseId || "",
  );

  const reviewCase = singleCaseQuery.data || reviewCases?.pages[0]?.cases[0];
  const isLoading = isLoadingList || (!!caseId && singleCaseQuery.isLoading);

  const onSubmit = () => {
    onClose();

    decisionsQueryKeys.forEach((queryKey) => {
      queryClient.invalidateQueries({
        queryKey,
      });
    });

    refreshEntity(queryClient, workspace.id, schema, env, entityId);
  };

  return (
    <AccordionRoot
      className={accordionRootClassName}
      defaultValue={["manual-review"]}
      type="multiple"
    >
      <AccordionItem
        title={
          <ManualReviewAccordionHeader
            action={
              reviewCase?.id && (
                <Tooltip placement="left" title="Open Review Case in new tab">
                  <Icon
                    icon={faArrowUpRightFromSquare}
                    size="xs"
                    onClick={() => {
                      const url =
                        getBaseUrl() +
                        getUrlToReviewCase(
                          orgId,
                          workspace.id,
                          flowId,
                          reviewCase?.id ?? "",
                          search,
                        );
                      window.open(url, "_blank");
                    }}
                  />
                </Tooltip>
              )
            }
            title="Review Case Details"
          />
        }
        value="manual-review"
        headerAsChild
      >
        <ReviewCaseDetails
          flowSlug={flowSlug}
          isLoading={isLoading}
          reviewCase={reviewCase}
        />
      </AccordionItem>
      <AccordionItem title="Highlights" value="highlights">
        <ManualReviewHighlights highlights={reviewCase?.highlights ?? []} />
      </AccordionItem>
      <AccordionItem title="Inspect Data" value="inspect-data">
        <ManualReviewInspectData
          baseUrl={workspace.base_url}
          flowSlug={flowSlug}
          id={reviewCase?.id ?? ""}
        />
      </AccordionItem>
      <AccordionItem className="p-0" title="Actions" value="actions">
        <FormSide
          description={reviewCase?.response.schema.description}
          isLoading={isLoading}
          nodeName={reviewCase?.node_name}
          noBorders
        >
          {reviewCase && (
            <ReviewCaseActionForm
              fileUpload={fileUpload}
              flowSlug={flowSlug}
              reviewCase={reviewCase}
              onSubmit={onSubmit}
            />
          )}
        </FormSide>
      </AccordionItem>
    </AccordionRoot>
  );
};

const refreshEntity = (
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  schema: string,
  env: Environment,
  entityId: string,
) => {
  // Try to invalidate the entity query right-away
  // and then one more time after 5 seconds in case if update was not immediate
  queryClient.invalidateQueries({
    queryKey: entitiesKeys.entity(workspaceId, schema, env, entityId),
  });

  setTimeout(() => {
    queryClient.invalidateQueries({
      queryKey: entitiesKeys.entity(workspaceId, schema, env, entityId),
    });
  }, 5 * 1000);

  setTimeout(() => {
    queryClient.invalidateQueries({
      queryKey: entitiesKeys.entity(workspaceId, schema, env, entityId),
    });
  }, 15 * 1000);
};
